import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGS, setLanguage } from "../i18n";
import api, { getToken } from "../lib/api";
import { toast } from "sonner";
import { Globe, Check, ChevronDown } from "lucide-react";

/**
 * Compact language switcher — flag + native name dropdown.
 * Persists to localStorage instantly, and (if signed in) to the user's profile.
 */
export default function LanguageSwitcher({ variant = "compact", onSelect }) {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const current = SUPPORTED_LANGS.find((l) => l.code === i18n.resolvedLanguage) || SUPPORTED_LANGS[0];

  async function pick(code) {
    // If parent supplied onSelect (e.g. FirstVisitLanguagePicker), delegate — parent is
    // responsible for setLanguage, toast, backend PUT, and modal close.
    if (onSelect) { onSelect(code); return; }
    setLanguage(code);
    setOpen(false);
    // Also mark the first-visit modal as seen so picking a language from any surface
    // (including the modal's own grid) dismisses the modal permanently.
    localStorage.setItem("hb_lang_seen", "1");
    const langLabel = SUPPORTED_LANGS.find((l) => l.code === code)?.native || code;
    toast.success(t("language.switched", { lang: langLabel }));
    if (getToken()) {
      try {
        await api.put("/auth/language", { language: code });
      } catch (err) {
        console.warn("Failed to sync language to server:", err);
      }
    }
  }

  if (variant === "grid") {
    // Full grid variant used in first-visit modal / settings
    return (
      <div className="grid grid-cols-2 gap-3" data-testid="language-grid">
        {SUPPORTED_LANGS.map((l) => (
          <button key={l.code}
                  onClick={() => pick(l.code)}
                  data-testid={`lang-option-${l.code}`}
                  className={`p-4 rounded-2xl border-2 text-left transition-all ${
                    current.code === l.code
                      ? "border-blue-500 bg-blue-50"
                      : "border-slate-200 bg-white hover:border-slate-400"
                  }`}>
            <div className="text-3xl">{l.flag}</div>
            <div className="mt-2 font-semibold text-slate-900">{l.native}</div>
            <div className="text-xs text-slate-500">{l.label}</div>
            {current.code === l.code && (
              <div className="mt-2 text-xs text-blue-700 font-semibold flex items-center gap-1">
                <Check className="w-3 h-3"/> {t("language.current")}
              </div>
            )}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className="relative" data-testid="language-switcher">
      <button
        onClick={() => setOpen((x) => !x)}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg hover:bg-slate-100 text-slate-700 text-sm font-medium"
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid="lang-switcher-btn"
      >
        <span className="text-base leading-none">{current.flag}</span>
        <span className="hidden sm:inline text-xs uppercase font-bold tracking-wider">{current.code}</span>
        <ChevronDown className="w-3.5 h-3.5" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 w-52 bg-white rounded-xl border border-slate-200 shadow-lg z-40 py-1" role="listbox">
            {SUPPORTED_LANGS.map((l) => (
              <button key={l.code}
                      onClick={() => pick(l.code)}
                      data-testid={`lang-item-${l.code}`}
                      role="option"
                      aria-selected={current.code === l.code}
                      className={`w-full text-left px-3 py-2 flex items-center gap-2 text-sm hover:bg-slate-50 ${
                        current.code === l.code ? "font-semibold text-slate-900" : "text-slate-700"
                      }`}>
                <span className="text-lg leading-none">{l.flag}</span>
                <span className="flex-1">{l.native}</span>
                {current.code === l.code && <Check className="w-4 h-4 text-blue-600"/>}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * First-visit language picker modal. Shows once (persisted flag) or when explicitly re-opened.
 */
export function FirstVisitLanguagePicker() {
  const { i18n, t } = useTranslation();
  const seen = typeof window !== "undefined" && localStorage.getItem("hb_lang_seen");
  const [open, setOpen] = useState(!seen);

  if (!open) return null;
  const deviceLang = (typeof navigator !== "undefined" ? navigator.language : "en").slice(0, 2);
  const suggested = SUPPORTED_LANGS.find((l) => l.code === deviceLang) || SUPPORTED_LANGS[0];

  async function confirm(code) {
    setLanguage(code);
    localStorage.setItem("hb_lang_seen", "1");
    if (getToken()) {
      try {
        await api.put("/auth/language", { language: code });
      } catch (err) {
        console.warn("Failed to sync language to server:", err);
      }
    }
    setOpen(false);
  }

  return (
    <div className="fixed inset-0 bg-slate-900/70 z-50 flex items-center justify-center p-4" data-testid="lang-first-visit-modal">
      <div className="bg-white rounded-3xl max-w-lg w-full p-8 hb-chunky-shadow">
        <div className="flex items-center gap-3">
          <Globe className="w-6 h-6 text-blue-600" />
          <h2 className="font-adult font-bold text-2xl text-slate-900">{t("language.chooseTitle")}</h2>
        </div>
        <p className="text-slate-600 text-sm mt-2">{t("language.chooseSubtitle")}</p>

        <div className="mt-4 p-3 rounded-xl bg-blue-50 border border-blue-100 text-sm text-slate-800">
          {t("language.detected")} <b>{suggested.native}</b> {suggested.flag}
          <button onClick={() => confirm(suggested.code)}
                  data-testid="lang-use-detected"
                  className="mt-2 block w-full rounded-full bg-slate-900 text-white text-sm font-semibold py-2 hover:bg-slate-800">
            {t("language.useThis")}
          </button>
        </div>

        <div className="mt-6">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">
            {t("language.orChoose")}
          </div>
          <LanguageSwitcher variant="grid" onSelect={confirm} />
        </div>
      </div>
    </div>
  );
}
